/**
 * DepartmentRenderer - Renders a single department section with all its content
 *
 * Extracted from ProjectionInputView for maintainability.
 * Handles all department types: New/Used Vehicle, Service, Parts, Body Shop, Rental, Others, Misc
 */

import React from 'react';
import {
  DepartmentTemplate,
  ExpenseCategory,
  AccountDefinition,
} from '../../config/docTemplate';
import { ROW_COLORS, DOCGrossData, AccountData } from './projectionInputTypes';
import { UnitCountsSection } from './UnitCountsSection';
import { ExpenseCategorySection } from './ExpenseCategorySection';
import { PerUnitInputCell } from './ProjectionInputCells';
import {
  DepartmentTotals,
  ServiceDeptProjections,
  CategoryAccountExpenses,
  CalculatedGross,
} from '../../hooks/useProjectionCalculations';
import { UnitCountsData } from '../../services/api';
import { HistoricalColumn } from '../../utils/historicalColumns';

interface DepartmentRendererProps {
  dept: DepartmentTemplate;
  /** One entry per display-year column, oldest first (3, or 4 during the editing window) */
  columns: HistoricalColumn[];
  targetYear: number;
  // State
  isExpanded: boolean;
  expandedCategories: Set<string>;
  isEditing: boolean;
  localInputs: Record<string, { value: number; inputType: 'amount' | 'percentage'; isDirty: boolean }>;
  // Handlers
  onToggleDepartment: (deptName: string) => void;
  onToggleCategory: (key: string) => void;
  onValueChange: (department: string, metric: string, value: number, inputType?: 'amount' | 'percentage') => void;
  // Calculation functions
  calculateDepartmentTotals: (
    dept: DepartmentTemplate,
    docGrossMap: Map<string, DOCGrossData>,
    accountDataMap: Map<string, AccountData>,
    unitCountsData: UnitCountsData | null
  ) => DepartmentTotals;
  getCalculatedGross: (department: string, metric: 'GROSS' | 'FI_INCOME') => CalculatedGross;
  getTotalProjectedUnits: (department: string) => number;
  getProjectionValue: (department: string, metric: string) => number | null;
  getProjectedUnitsByBrand: (department: string) => Record<string, number>;
  getServiceDeptProjections: (department: string) => ServiceDeptProjections;
  getCategoryAccountExpenses: (
    department: string,
    accounts: AccountDefinition[],
    projectedGross: number
  ) => CategoryAccountExpenses;
  getAccountExpensePercent: (department: string, accountCode: string) => number | null;
  getAccountWithSign: (
    def: AccountDefinition,
    categoryName: string,
    accountDataMap: Map<string, AccountData>
  ) => number;
  calculateCategoryTotals: (
    category: ExpenseCategory,
    accountDataMap: Map<string, AccountData>
  ) => number;
  // Formatters
  formatCurrency: (value: number) => string;
}

/**
 * Format percentage for display
 */
const formatPercent = (value: number, gross: number): string => {
  if (gross === 0) return '--';
  const percent = (value / gross) * 100;
  return `${percent.toFixed(1)}%`;
};

/**
 * Format per-unit currency for display
 */
const formatPerUnit = (value: number, units: number, formatCurrency: (v: number) => string): string => {
  if (units === 0) return '--';
  const perUnit = value / units;
  return formatCurrency(perUnit);
};

/**
 * Renders a data row with one YTD value per display-year column
 */
const DataRow: React.FC<{
  label: string;
  values: number[];
  colorClass: string;
  isBold?: boolean;
  indent?: number;
  showPercentColumn?: boolean;
  formatCurrency: (value: number) => string;
  renderInputCell?: () => React.ReactNode;
}> = ({
  label,
  values,
  colorClass,
  isBold = false,
  indent = 0,
  showPercentColumn = false,
  formatCurrency,
  renderInputCell,
}) => (
  <tr className={`${colorClass} border-b border-gray-200`}>
    <td
      className={`min-w-[200px] px-3 py-2 text-sm ${isBold ? 'font-semibold' : ''} sticky left-0 ${colorClass}`}
      style={{ paddingLeft: `${12 + indent * 16}px` }}
    >
      {label}
    </td>
    {values.map((value, i) => {
      const isNewest = i === values.length - 1;
      const weight = isNewest ? (isBold ? 'font-bold' : 'font-semibold') : (isBold ? 'font-semibold' : '');
      return (
        <React.Fragment key={i}>
          <td className={`min-w-[100px] px-2 py-2 text-right text-sm ${weight}`}>
            {formatCurrency(value)}
          </td>
          {showPercentColumn && <td className="min-w-[60px]"></td>}
        </React.Fragment>
      );
    })}
    {renderInputCell ? (
      renderInputCell()
    ) : (
      <td className={`min-w-[120px] px-2 py-2 text-right text-sm font-semibold ${colorClass}`}>
        {/* No input */}
      </td>
    )}
  </tr>
);

/**
 * Main department renderer component
 */
export const DepartmentRenderer: React.FC<DepartmentRendererProps> = ({
  dept,
  columns,
  targetYear,
  isExpanded,
  expandedCategories,
  isEditing,
  localInputs,
  onToggleDepartment,
  onToggleCategory,
  onValueChange,
  calculateDepartmentTotals,
  getCalculatedGross,
  getTotalProjectedUnits,
  getProjectionValue,
  getProjectedUnitsByBrand,
  getServiceDeptProjections,
  getCategoryAccountExpenses,
  getAccountExpensePercent,
  getAccountWithSign,
  calculateCategoryTotals,
  formatCurrency,
}) => {
  // Calculate totals for each display-year column (oldest first)
  const colTotals: DepartmentTotals[] = columns.map(c =>
    calculateDepartmentTotals(dept, c.data.docGrossMap, c.data.accountDataMap, c.data.unitCountsData)
  );
  const lastIdx = columns.length - 1;
  const newestTotals = colTotals[lastIdx];
  const accountMaps = columns.map(c => c.data.accountDataMap);

  // An account participates in projected totals if any display year has it or it carries
  // an input. Keep in sync with SeasonalityModeView.hasAccountData (the values that get saved).
  const hasAccountData = (code: string): boolean =>
    accountMaps.some(m => m.has(code)) ||
    getProjectionValue(dept.name, `EXPENSE_${code}`) !== null ||
    getProjectionValue(dept.name, `INCOME_${code}`) !== null;

  // Has expenses to show? (for expense category % of gross)
  const hasExpenses = dept.name !== 'OTHERS' && dept.name !== 'MISC';
  // Show % column for expense categories
  const showPercentColumn = hasExpenses;
  // OTHERS department still needs % column headers for % of location gross
  const showPercentColumnHeader = dept.name !== 'MISC';

  // Calculate projected values from per-unit inputs (for New/Used depts) or direct inputs (for others)
  let projectedGross = 0;
  let projectedUnits = 0;

  if (dept.hasFandI) {
    // For New/Used Vehicle departments - calculate from per-unit values
    const grossCalc = getCalculatedGross(dept.name, 'GROSS');
    const fiCalc = getCalculatedGross(dept.name, 'FI_INCOME');
    projectedGross = grossCalc.total + fiCalc.total;
    projectedUnits = getTotalProjectedUnits(dept.name);
  } else if (dept.name === 'RENTAL') {
    // For Rental department - use rental income as the "gross" for expense calculations
    projectedGross = getProjectionValue(dept.name, 'RENTAL_INCOME') || 0;
  } else if (dept.name === 'OTHERS') {
    // For Others department - sum up all account incomes including DOC fee calculation
    const otherIncomeCategory = dept.expenseCategories.find(cat => cat.name === 'OTHER_INCOME');
    if (otherIncomeCategory) {
      const totalProjectedNewUnits = getTotalProjectedUnits('NEW_VEHICLE');
      const totalProjectedUsedUnits = getTotalProjectedUnits('USED_VEHICLE');
      const totalUnits = totalProjectedNewUnits + totalProjectedUsedUnits;

      otherIncomeCategory.accounts.forEach(acc => {
        if (hasAccountData(acc.code)) {
          if (acc.code === '850') {
            // DOC fee: per-unit amount × total units
            const docFeePerUnit = getProjectionValue(dept.name, 'INCOME_850_PER_UNIT') || 0;
            projectedGross += docFeePerUnit * totalUnits;
          } else {
            // Regular account: direct $ amount
            projectedGross += getProjectionValue(dept.name, `INCOME_${acc.code}`) || 0;
          }
        }
      });
    }
  } else {
    // For other departments - use direct input values
    projectedGross = getProjectionValue(dept.name, 'GROSS') || 0;
  }

  // Calculate projected expenses from account-level percentage inputs
  let projectedExpenses = 0;
  let projectedExpensePercent = 0;

  // Sum up all account-level expenses for each expense category
  dept.expenseCategories
    .filter(cat => !['RENTAL_INCOME', 'OTHER_INCOME'].includes(cat.name))
    .forEach(cat => {
      const accountsWithData = cat.accounts.filter(acc => {
        return hasAccountData(acc.code);
      });
      const categoryExpenses = getCategoryAccountExpenses(dept.name, accountsWithData, projectedGross);
      projectedExpenses += categoryExpenses.totalAmount;
      projectedExpensePercent += categoryExpenses.totalPercent;
    });

  const projectedProfit = projectedGross - projectedExpenses;
  const hasProjections = projectedGross > 0 || projectedExpenses > 0;
  const projectedProfitColor = projectedProfit >= 0 ? 'text-green-600' : 'text-red-600';

  // One YTD cell (+ empty % cell) per display year for an account row
  const renderAccountYearCells = (code: string) =>
    accountMaps.map((map, i) => (
      <React.Fragment key={i}>
        <td className={`min-w-[100px] px-2 py-1.5 text-right text-xs ${i === lastIdx ? 'font-medium' : 'text-gray-700'}`}>
          {formatCurrency(map.get(code)?.ytdTotal || 0)}
        </td>
        <td className="min-w-[60px]"></td>
      </React.Fragment>
    ));

  // Helper to render expense category
  const renderExpenseCategory = (
    category: ExpenseCategory,
    yearGross: number[],
    showInput: boolean = true,
    projectedDeptGross: number = 0
  ) => {
    const categoryKey = `${dept.name}-${category.name}`;
    return (
      <ExpenseCategorySection
        key={categoryKey}
        dept={dept}
        category={category}
        yearGross={yearGross}
        showInput={showInput}
        projectedDeptGross={projectedDeptGross}
        isExpanded={expandedCategories.has(categoryKey)}
        onToggleCategory={onToggleCategory}
        yearAccountDataMaps={accountMaps}
        calculateCategoryTotals={calculateCategoryTotals}
        getAccountWithSign={getAccountWithSign}
        getCategoryAccountExpenses={getCategoryAccountExpenses}
        getAccountExpensePercent={getAccountExpensePercent}
        isEditing={isEditing}
        localInputs={localInputs}
        formatCurrency={formatCurrency}
        onValueChange={onValueChange}
      />
    );
  };

  // Helper to render per-unit input cell
  const renderPerUnitInputCell = (metric: 'GROSS' | 'FI_INCOME', colorClass: string) => {
    const perUnitMetric = `${metric}_PER_UNIT`;
    const perUnitValue = getProjectionValue(dept.name, perUnitMetric);
    const totalUnits = getTotalProjectedUnits(dept.name);
    const key = `${dept.name}#${perUnitMetric}`;
    const isDirty = localInputs[key]?.isDirty || false;

    return (
      <PerUnitInputCell
        department={dept.name}
        metric={metric}
        perUnitValue={perUnitValue}
        totalUnits={totalUnits}
        isEditing={isEditing}
        isDirty={isDirty}
        colorClass={colorClass}
        formatCurrency={formatCurrency}
        onValueChange={onValueChange}
      />
    );
  };

  return (
    <div key={dept.name} className="bg-white rounded-lg shadow mb-4">
      {/* Department Header */}
      <div
        className="px-4 py-3 border-b border-gray-200 cursor-pointer hover:bg-gray-50"
        onClick={() => onToggleDepartment(dept.name)}
      >
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <span className="text-lg text-gray-400">
              {isExpanded ? '▼' : '▶'}
            </span>
            <h3 className="text-lg font-semibold text-gray-900">
              {dept.name === 'MISC' ? dept.displayName : `${dept.displayName} Department`}
              {(dept.name === 'NEW_VEHICLE' || dept.name === 'USED_VEHICLE') && projectedUnits > 0 && (
                <span className="ml-2 text-sm font-normal text-gray-500">
                  ({projectedUnits.toLocaleString()} units projected)
                </span>
              )}
            </h3>
          </div>
          <div className="text-right">
            {/* Show projected values if any projections entered, otherwise show placeholder */}
            {hasProjections ? (
              <>
                <div className={`text-lg font-bold ${dept.name === 'MISC' ? 'text-gray-700' : dept.name === 'OTHERS' ? 'text-green-600' : projectedProfitColor}`}>
                  {formatCurrency(
                    dept.name === 'MISC' ? projectedExpenses :
                    dept.name === 'OTHERS' ? projectedGross :
                    projectedProfit
                  )}
                  <span className="text-xs font-normal text-blue-600 ml-1">({targetYear} Projection)</span>
                </div>
                {dept.name !== 'MISC' && dept.name !== 'OTHERS' && (
                  <div className="text-xs text-gray-500">
                    Gross: {formatCurrency(projectedGross)} | Exp: {formatCurrency(projectedExpenses)}
                  </div>
                )}
              </>
            ) : (
              <div className="text-sm text-gray-400 italic">
                No projections entered
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            {/* Column Headers */}
            <thead className="sticky top-0 z-10">
              <tr className="bg-gray-700 text-white text-xs">
                <th className="min-w-[200px] px-3 py-2 text-left font-semibold sticky left-0 bg-gray-700 z-20">
                  Account / Category
                </th>
                {columns.map((col, i) => (
                  <React.Fragment key={col.year}>
                    <th className={`min-w-[100px] px-2 py-2 text-right ${i === lastIdx ? 'font-bold bg-gray-800' : 'font-semibold'}`}>
                      {col.label}
                    </th>
                    {showPercentColumnHeader && (
                      <th className={`min-w-[60px] px-1 py-2 text-right font-semibold text-gray-400 ${i === lastIdx ? 'bg-gray-800' : ''}`}>
                        %
                      </th>
                    )}
                  </React.Fragment>
                ))}
                <th className="min-w-[120px] px-2 py-2 text-right font-bold bg-blue-600">
                  {targetYear} Projection
                </th>
              </tr>
            </thead>
            <tbody>
              {/* Unit Counts (for New/Used Vehicle departments) */}
              <UnitCountsSection
                deptName={dept.name}
                yearData={columns.map(c => c.data)}
                projectedBrandUnits={getProjectedUnitsByBrand(dept.name)}
                totalProjectedUnits={getTotalProjectedUnits(dept.name)}
                isEditing={isEditing}
                localInputs={localInputs}
                onValueChange={onValueChange}
              />

              {/* NEW/USED DEPARTMENTS: Sales, Gross (Frontend), F&I (Backend), Total Gross */}
              {dept.hasFandI && (
                <>
                  {/* Sales - read-only display */}
                  <DataRow
                    label="Sales YTD"
                    values={colTotals.map(t => t.sales)}
                    colorClass={ROW_COLORS.sales}
                    isBold={true}
                    showPercentColumn={showPercentColumn}
                    formatCurrency={formatCurrency}
                  />
                  {/* Gross (Frontend) with per-unit */}
                  <tr className={`${ROW_COLORS.gross} border-b border-gray-200`}>
                    <td className={`min-w-[200px] px-3 py-2 text-sm font-semibold sticky left-0 ${ROW_COLORS.gross}`}>
                      Gross (Frontend) YTD
                    </td>
                    {colTotals.map((t, i) => (
                      <React.Fragment key={i}>
                        <td className={`min-w-[140px] px-2 py-2 text-right text-sm whitespace-nowrap ${i === lastIdx ? 'font-bold' : 'font-semibold'}`}>
                          {formatCurrency(t.frontGross)} <span className="text-xs text-gray-500 font-normal">{formatPerUnit(t.frontGross, t.units, formatCurrency)}/u</span>
                        </td>
                        {showPercentColumn && <td className="min-w-[60px]"></td>}
                      </React.Fragment>
                    ))}
                    {renderPerUnitInputCell('GROSS', ROW_COLORS.gross)}
                  </tr>
                  {/* F&I (Backend) with per-unit */}
                  <tr className={`${ROW_COLORS.fiIncome} border-b border-gray-200`}>
                    <td className={`min-w-[200px] px-3 py-2 text-sm font-semibold sticky left-0 ${ROW_COLORS.fiIncome}`}>
                      F&I (Backend) YTD
                    </td>
                    {colTotals.map((t, i) => (
                      <React.Fragment key={i}>
                        <td className={`min-w-[140px] px-2 py-2 text-right text-sm whitespace-nowrap ${i === lastIdx ? 'font-bold' : 'font-semibold'}`}>
                          {formatCurrency(t.fiIncome)} <span className="text-xs text-gray-500 font-normal">{formatPerUnit(t.fiIncome, t.units, formatCurrency)}/u</span>
                        </td>
                        {showPercentColumn && <td className="min-w-[60px]"></td>}
                      </React.Fragment>
                    ))}
                    {renderPerUnitInputCell('FI_INCOME', ROW_COLORS.fiIncome)}
                  </tr>
                  {/* Total Gross - calculated from per-unit inputs */}
                  <tr className={`${ROW_COLORS.totalGross} border-b border-gray-200`}>
                    <td className={`min-w-[200px] px-3 py-2 text-sm font-bold sticky left-0 ${ROW_COLORS.totalGross}`}>
                      Total Gross
                    </td>
                    {colTotals.map((t, i) => (
                      <React.Fragment key={i}>
                        <td className="min-w-[100px] px-2 py-2 text-right text-sm font-bold">
                          {formatCurrency(t.totalGross)}
                        </td>
                        {showPercentColumn && (
                          <td className="min-w-[60px] px-1 py-2 text-right text-xs text-green-700 font-semibold">
                            {formatPercent(t.totalGross, columns[i].locationTotalGross)}
                          </td>
                        )}
                      </React.Fragment>
                    ))}
                    <td className="min-w-[120px] px-2 py-2 text-right text-sm font-bold bg-green-100 text-green-800">
                      {/* Calculate from per-unit GROSS + FI_INCOME projections */}
                      {(() => {
                        const grossCalc = getCalculatedGross(dept.name, 'GROSS');
                        const fiCalc = getCalculatedGross(dept.name, 'FI_INCOME');
                        const total = grossCalc.total + fiCalc.total;
                        return total > 0 ? formatCurrency(total) : '--';
                      })()}
                    </td>
                  </tr>
                </>
              )}

              {/* SERVICE/PARTS/BODY SHOP DEPARTMENTS: Sales (calculated), Gross (input with % to sales) */}
              {dept.hasRevenue && !dept.hasFandI && (
                <>
                  {/* Sales row - calculated from Gross / (Gross-to-Sales %) */}
                  {(() => {
                    const serviceDeptProj = getServiceDeptProjections(dept.name);
                    return (
                      <tr className={`${ROW_COLORS.sales} border-b border-gray-200`}>
                        <td className={`min-w-[200px] px-3 py-2 text-sm font-semibold sticky left-0 ${ROW_COLORS.sales}`}>
                          Sales YTD
                        </td>
                        {colTotals.map((t, i) => (
                          <React.Fragment key={i}>
                            <td className={`min-w-[100px] px-2 py-2 text-right text-sm ${i === lastIdx ? 'font-bold' : 'font-semibold'}`}>
                              {formatCurrency(t.sales)}
                            </td>
                            {showPercentColumn && <td className="min-w-[60px]"></td>}
                          </React.Fragment>
                        ))}
                        {/* Calculated Sales - read-only */}
                        <td className={`min-w-[120px] px-2 py-2 text-right text-sm font-bold ${ROW_COLORS.sales}`}>
                          {serviceDeptProj.calculatedSales > 0 ? formatCurrency(serviceDeptProj.calculatedSales) : '--'}
                        </td>
                      </tr>
                    );
                  })()}
                  {/* Gross row with input for $ amount and % gross-to-sales */}
                  {(() => {
                    const serviceDeptProj = getServiceDeptProjections(dept.name);
                    const grossKey = `${dept.name}#GROSS`;
                    const pctKey = `${dept.name}#GROSS_TO_SALES_PCT`;
                    const grossIsDirty = localInputs[grossKey]?.isDirty || false;
                    const pctIsDirty = localInputs[pctKey]?.isDirty || false;


                    return (
                      <tr className={`${ROW_COLORS.gross} border-b border-gray-200`}>
                        <td className={`min-w-[200px] px-3 py-2 text-sm font-bold sticky left-0 ${ROW_COLORS.gross}`}>
                          Gross YTD
                        </td>
                        {colTotals.map((t, i) => {
                          // Historical gross-to-sales percentage
                          const grossToSales = t.sales > 0 ? (t.frontGross / t.sales) * 100 : 0;
                          return (
                            <React.Fragment key={i}>
                              <td className="min-w-[100px] px-2 py-2 text-right text-sm font-bold whitespace-nowrap">
                                {formatCurrency(t.frontGross)}
                                <span className="text-xs text-gray-500 font-normal ml-1">{grossToSales.toFixed(1)}%</span>
                              </td>
                              {showPercentColumn && (
                                <td className="min-w-[60px] px-1 py-2 text-right text-xs text-green-700 font-semibold">
                                  {formatPercent(t.frontGross, columns[i].locationTotalGross)}
                                </td>
                              )}
                            </React.Fragment>
                          );
                        })}
                        {/* Gross input with % gross-to-sales */}
                        {!isEditing ? (
                          <td className={`min-w-[120px] px-2 py-2 text-right text-sm font-bold ${ROW_COLORS.gross}`}>
                            {serviceDeptProj.gross > 0 ? (
                              <div className="whitespace-nowrap">
                                <span>{formatCurrency(serviceDeptProj.gross)}</span>
                                <span className="text-xs font-normal ml-1">{serviceDeptProj.grossToSalesPercent.toFixed(1)}%</span>
                              </div>
                            ) : '--'}
                          </td>
                        ) : (
                          <td className={`min-w-[140px] px-1 py-1 ${ROW_COLORS.gross}`}>
                            <div className="flex flex-col space-y-1">
                              {/* Gross $ input */}
                              <div className="flex items-center">
                                <span className="text-xs text-gray-500 mr-1">$:</span>
                                <input
                                  type="number"
                                  step="1000"
                                  value={serviceDeptProj.gross || ''}
                                  onChange={(e) => {
                                    const newValue = parseFloat(e.target.value) || 0;
                                    onValueChange(dept.name, 'GROSS', newValue, 'amount');
                                  }}
                                  className={`w-full px-1 py-0.5 text-right text-xs border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                                    grossIsDirty ? 'border-yellow-400 bg-yellow-50' : 'border-gray-300'
                                  }`}
                                  placeholder="0"
                                />
                              </div>
                              {/* Gross-to-Sales % input */}
                              <div className="flex items-center">
                                <span className="text-xs text-gray-500 mr-1">%:</span>
                                <input
                                  type="number"
                                  step="0.1"
                                  value={serviceDeptProj.grossToSalesPercent || ''}
                                  onChange={(e) => {
                                    const newValue = parseFloat(e.target.value) || 0;
                                    onValueChange(dept.name, 'GROSS_TO_SALES_PCT', newValue, 'percentage');
                                  }}
                                  className={`w-full px-1 py-0.5 text-right text-xs border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                                    pctIsDirty ? 'border-yellow-400 bg-yellow-50' : 'border-gray-300'
                                  }`}
                                  placeholder="0.0"
                                />
                              </div>
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })()}
                </>
              )}

              {/* RENTAL DEPARTMENT: Rental Income with input */}
              {dept.name === 'RENTAL' && (
                <>
                  {(() => {
                    // Get the rental income category to display accounts
                    const rentalIncomeCategory = dept.expenseCategories.find(cat => cat.name === 'RENTAL_INCOME');
                    if (!rentalIncomeCategory) return null;

                    const categoryKey = `${dept.name}-RENTAL_INCOME`;
                    const isCategoryExpanded = expandedCategories.has(categoryKey);

                    // Get accounts with data
                    const accountsWithData = rentalIncomeCategory.accounts.filter(acc => {
                      return hasAccountData(acc.code);
                    });

                    // Get projected rental income
                    const projectedRentalIncome = getProjectionValue(dept.name, 'RENTAL_INCOME') || 0;
                    const rentalIncomeKey = `${dept.name}#RENTAL_INCOME`;
                    const isDirty = localInputs[rentalIncomeKey]?.isDirty || false;

                    return (
                      <>
                        {/* Rental Income Header Row */}
                        <tr
                          className={`${ROW_COLORS.rentalIncome} border-b border-gray-200 ${accountsWithData.length > 0 ? 'cursor-pointer hover:opacity-90' : ''}`}
                          onClick={() => accountsWithData.length > 0 && onToggleCategory(categoryKey)}
                        >
                          <td className={`min-w-[200px] px-3 py-2 text-sm font-bold sticky left-0 ${ROW_COLORS.rentalIncome}`} style={{ paddingLeft: '28px' }}>
                            <div className="flex items-center">
                              {accountsWithData.length > 0 && (
                                <span className="mr-2 text-gray-500 w-4 flex-shrink-0">
                                  {isCategoryExpanded ? '▼' : '▶'}
                                </span>
                              )}
                              {accountsWithData.length === 0 && <span className="mr-2 w-4 flex-shrink-0"></span>}
                              <span>Rental Vehicle Income</span>
                              {accountsWithData.length > 0 && (
                                <span className="ml-2 text-xs text-gray-500">({accountsWithData.length})</span>
                              )}
                            </div>
                          </td>
                          {colTotals.map((t, i) => (
                            <React.Fragment key={i}>
                              <td className="min-w-[100px] px-2 py-2 text-right text-sm font-bold">
                                {formatCurrency(t.rentalIncome)}
                              </td>
                              <td className="min-w-[60px]"></td>
                            </React.Fragment>
                          ))}
                          {/* Rental Income Input */}
                          {!isEditing ? (
                            <td className={`min-w-[120px] px-2 py-2 text-right text-sm font-bold ${ROW_COLORS.rentalIncome}`}>
                              {projectedRentalIncome > 0 ? formatCurrency(projectedRentalIncome) : '--'}
                            </td>
                          ) : (
                            <td className={`min-w-[120px] px-1 py-1 ${ROW_COLORS.rentalIncome}`}>
                              <input
                                type="number"
                                step="1000"
                                value={projectedRentalIncome || ''}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => {
                                  const newValue = parseFloat(e.target.value) || 0;
                                  onValueChange(dept.name, 'RENTAL_INCOME', newValue, 'amount');
                                }}
                                className={`w-full px-2 py-1 text-right text-sm border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                                  isDirty ? 'border-yellow-400 bg-yellow-50' : 'border-gray-300'
                                }`}
                                placeholder="0"
                              />
                            </td>
                          )}
                        </tr>

                        {/* Expanded Account Rows */}
                        {isCategoryExpanded && accountsWithData.map(acc => {
                          const accountYearCells = renderAccountYearCells(acc.code);

                          return (
                            <tr key={acc.code} className="bg-gray-50 border-b border-gray-100 hover:bg-gray-100">
                              <td className="min-w-[200px] px-3 py-1.5 text-xs sticky left-0 bg-gray-50" style={{ paddingLeft: '44px' }}>
                                <div className="flex items-center">
                                  <span className="font-mono text-gray-600 mr-2">{acc.code}</span>
                                  <span className="text-gray-500 truncate">{acc.description}</span>
                                </div>
                              </td>
                              {accountYearCells}
                              <td className="min-w-[120px] bg-gray-50"></td>
                            </tr>
                          );
                        })}
                      </>
                    );
                  })()}
                </>
              )}

              {/* OTHERS DEPARTMENT: Expandable accounts with $ inputs */}
              {dept.name === 'OTHERS' && (
                <>
                  {(() => {
                    // Get the OTHER_INCOME category
                    const otherIncomeCategory = dept.expenseCategories.find(cat => cat.name === 'OTHER_INCOME');
                    if (!otherIncomeCategory) return null;

                    const categoryKey = `${dept.name}-OTHER_INCOME`;
                    const isCategoryExpanded = expandedCategories.has(categoryKey);

                    // Get accounts with data
                    const accountsWithData = otherIncomeCategory.accounts.filter(acc => {
                      return hasAccountData(acc.code);
                    });

                    // Calculate total projected New + Used units for DOC fee calculation
                    const totalProjectedNewUnits = getTotalProjectedUnits('NEW_VEHICLE');
                    const totalProjectedUsedUnits = getTotalProjectedUnits('USED_VEHICLE');
                    const totalProjectedUnits = totalProjectedNewUnits + totalProjectedUsedUnits;

                    // Calculate projected Others total from account inputs
                    let projectedOthersTotal = 0;
                    accountsWithData.forEach(acc => {
                      if (acc.code === '850') {
                        // DOC fee: per-unit amount × total units
                        const docFeePerUnit = getProjectionValue(dept.name, 'INCOME_850_PER_UNIT') || 0;
                        projectedOthersTotal += docFeePerUnit * totalProjectedUnits;
                      } else {
                        // Regular account: direct $ amount
                        const accValue = getProjectionValue(dept.name, `INCOME_${acc.code}`) || 0;
                        projectedOthersTotal += accValue;
                      }
                    });

                    return (
                      <>
                        {/* Others Total Header Row */}
                        <tr
                          className={`${ROW_COLORS.totalGross} border-b border-gray-200 ${accountsWithData.length > 0 ? 'cursor-pointer hover:opacity-90' : ''}`}
                          onClick={() => accountsWithData.length > 0 && onToggleCategory(categoryKey)}
                        >
                          <td className={`min-w-[200px] px-3 py-2 text-sm font-bold sticky left-0 ${ROW_COLORS.totalGross}`}>
                            <div className="flex items-center">
                              {accountsWithData.length > 0 && (
                                <span className="mr-2 text-gray-500 w-4 flex-shrink-0">
                                  {isCategoryExpanded ? '▼' : '▶'}
                                </span>
                              )}
                              {accountsWithData.length === 0 && <span className="mr-2 w-4 flex-shrink-0"></span>}
                              <span className="text-green-700 font-bold">Others Total</span>
                              {accountsWithData.length > 0 && (
                                <span className="ml-2 text-xs text-gray-500">({accountsWithData.length})</span>
                              )}
                            </div>
                          </td>
                          {colTotals.map((t, i) => (
                            <React.Fragment key={i}>
                              <td className="min-w-[100px] px-2 py-2 text-right text-sm font-bold">
                                {formatCurrency(t.totalGross)}
                              </td>
                              <td className="min-w-[60px] px-1 py-2 text-right text-xs text-green-700 font-semibold">
                                {formatPercent(t.totalGross, columns[i].locationTotalGross)}
                              </td>
                            </React.Fragment>
                          ))}
                          {/* Auto-calculated total from account inputs */}
                          <td className="min-w-[120px] px-2 py-2 text-right text-sm font-bold bg-green-100 text-green-800">
                            {projectedOthersTotal > 0 ? formatCurrency(projectedOthersTotal) : '--'}
                          </td>
                        </tr>

                        {/* Expanded Account Rows with inputs */}
                        {isCategoryExpanded && accountsWithData.map(acc => {
                          const accountYearCells = renderAccountYearCells(acc.code);

                          // Special handling for DOC fee (850)
                          const isDocFee = acc.code === '850';

                          if (isDocFee) {
                            // DOC Fee: enter per-unit amount, calculate total
                            const docFeePerUnit = getProjectionValue(dept.name, 'INCOME_850_PER_UNIT');
                            const calculatedTotal = (docFeePerUnit || 0) * totalProjectedUnits;
                            const docFeeKey = `${dept.name}#INCOME_850_PER_UNIT`;
                            const isDirty = localInputs[docFeeKey]?.isDirty || false;

                            return (
                              <tr key={acc.code} className="bg-gray-50 border-b border-gray-100 hover:bg-gray-100">
                                <td className="min-w-[200px] px-3 py-1.5 text-xs sticky left-0 bg-gray-50" style={{ paddingLeft: '44px' }}>
                                  <div className="flex items-center">
                                    <span className="font-mono text-gray-600 mr-2">{acc.code}</span>
                                    <span className="text-gray-500 truncate">{acc.description}</span>
                                  </div>
                                </td>
                                {accountYearCells}
                                {/* DOC Fee Input - per unit */}
                                {!isEditing ? (
                                  <td className="min-w-[120px] px-2 py-1.5 text-right text-xs bg-gray-50">
                                    {docFeePerUnit !== null && totalProjectedUnits > 0 ? (
                                      <div className="whitespace-nowrap">
                                        <span className="font-semibold">{formatCurrency(calculatedTotal)}</span>
                                        <span className="text-gray-500 ml-1">${docFeePerUnit}/u</span>
                                      </div>
                                    ) : '--'}
                                  </td>
                                ) : (
                                  <td className="min-w-[140px] px-1 py-1 bg-gray-50">
                                    <div className="flex flex-col">
                                      {/* Calculated total */}
                                      <div className="text-right text-xs font-semibold text-green-700 mb-0.5">
                                        {totalProjectedUnits > 0 && docFeePerUnit ? (
                                          <span>{formatCurrency(calculatedTotal)} ({totalProjectedUnits} units)</span>
                                        ) : (
                                          <span className="text-gray-400">Enter units first</span>
                                        )}
                                      </div>
                                      {/* Per-unit input */}
                                      <div className="flex items-center justify-end">
                                        <span className="text-xs text-gray-500 mr-1">$/u:</span>
                                        <input
                                          type="number"
                                          step="1"
                                          value={docFeePerUnit ?? ''}
                                          onClick={(e) => e.stopPropagation()}
                                          onChange={(e) => {
                                            const newValue = parseFloat(e.target.value) || 0;
                                            onValueChange(dept.name, 'INCOME_850_PER_UNIT', newValue, 'amount');
                                          }}
                                          className={`w-20 px-1 py-0.5 text-right text-xs border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                                            isDirty ? 'border-yellow-400 bg-yellow-50' : 'border-gray-300'
                                          }`}
                                          placeholder="599"
                                        />
                                      </div>
                                    </div>
                                  </td>
                                )}
                              </tr>
                            );
                          }

                          // Regular account - direct $ input
                          const accValue = getProjectionValue(dept.name, `INCOME_${acc.code}`);
                          const accKey = `${dept.name}#INCOME_${acc.code}`;
                          const isDirty = localInputs[accKey]?.isDirty || false;

                          return (
                            <tr key={acc.code} className="bg-gray-50 border-b border-gray-100 hover:bg-gray-100">
                              <td className="min-w-[200px] px-3 py-1.5 text-xs sticky left-0 bg-gray-50" style={{ paddingLeft: '44px' }}>
                                <div className="flex items-center">
                                  <span className="font-mono text-gray-600 mr-2">{acc.code}</span>
                                  <span className="text-gray-500 truncate">{acc.description}</span>
                                </div>
                              </td>
                              {accountYearCells}
                              {/* Regular $ input */}
                              {!isEditing ? (
                                <td className="min-w-[120px] px-2 py-1.5 text-right text-xs bg-gray-50">
                                  {accValue !== null && accValue > 0 ? formatCurrency(accValue) : '--'}
                                </td>
                              ) : (
                                <td className="min-w-[120px] px-1 py-1 bg-gray-50">
                                  <input
                                    type="number"
                                    step="1000"
                                    value={accValue ?? ''}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => {
                                      const newValue = parseFloat(e.target.value) || 0;
                                      onValueChange(dept.name, `INCOME_${acc.code}`, newValue, 'amount');
                                    }}
                                    className={`w-full px-2 py-1 text-right text-xs border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                                      isDirty ? 'border-yellow-400 bg-yellow-50' : 'border-gray-300'
                                    }`}
                                    placeholder="0"
                                  />
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </>
                    );
                  })()}
                </>
              )}

              {/* EXPENSE CATEGORIES */}
              {dept.name === 'RENTAL' && (
                <>
                  {(() => {
                    // Get projected rental income for expense calculations
                    const projectedRentalIncome = getProjectionValue(dept.name, 'RENTAL_INCOME') || 0;
                    return (
                      <>
                        {dept.expenseCategories
                          .filter(cat => cat.name === 'RENTAL_SELLING')
                          .map(cat => renderExpenseCategory(cat,
                            colTotals.map(t => t.rentalIncome),
                            true,
                            projectedRentalIncome
                          ))}
                        {dept.expenseCategories
                          .filter(cat => cat.name === 'RENTAL_FIXED')
                          .map(cat => renderExpenseCategory(cat,
                            colTotals.map(t => t.rentalIncome),
                            true,
                            projectedRentalIncome
                          ))}
                      </>
                    );
                  })()}
                </>
              )}

              {dept.name !== 'RENTAL' && dept.name !== 'OTHERS' && dept.expenseCategories
                .filter(cat => !['RENTAL_INCOME', 'OTHER_INCOME'].includes(cat.name))
                .map(cat => renderExpenseCategory(cat, colTotals.map(t => t.totalGross), true, projectedGross))}

              {/* Total Expenses */}
              {dept.name !== 'MISC' && dept.name !== 'OTHERS' && (
                <tr className={`${ROW_COLORS.totalExpense} border-b border-gray-200`}>
                  <td className={`min-w-[200px] px-3 py-2 text-sm font-bold sticky left-0 ${ROW_COLORS.totalExpense}`}>
                    Total Expenses
                  </td>
                  {colTotals.map((t, i) => (
                    <React.Fragment key={i}>
                      <td className="min-w-[100px] px-2 py-2 text-right text-sm font-bold">
                        {formatCurrency(t.totalExpenses)}
                      </td>
                      {showPercentColumn && (
                        <td className="min-w-[60px] px-1 py-2 text-right text-xs text-gray-500">
                          {formatPercent(t.totalExpenses, t.totalGross || t.rentalIncome)}
                        </td>
                      )}
                    </React.Fragment>
                  ))}
                  <td className={`min-w-[120px] px-2 py-2 text-right text-sm font-bold ${ROW_COLORS.totalExpense}`}>
                    {/* Calculate from account-level expense projections */}
                    {projectedExpenses > 0 ? (
                      <div className="whitespace-nowrap">
                        <span>{formatCurrency(projectedExpenses)}</span>
                        <span className="text-xs font-normal ml-1">{projectedExpensePercent.toFixed(1)}%</span>
                      </div>
                    ) : '--'}
                  </td>
                </tr>
              )}

              {/* Operating Profit */}
              {dept.name !== 'MISC' && dept.name !== 'OTHERS' && (
                <tr className={(newestTotals?.operatingProfit ?? 0) >= 0 ? ROW_COLORS.profit : ROW_COLORS.loss}>
                  <td className={`min-w-[200px] px-3 py-2 text-sm font-bold sticky left-0 ${(newestTotals?.operatingProfit ?? 0) >= 0 ? ROW_COLORS.profit : ROW_COLORS.loss}`}>
                    Operating Profit
                  </td>
                  {colTotals.map((t, i) => (
                    <React.Fragment key={i}>
                      <td className={`min-w-[100px] px-2 py-2 text-right text-sm font-bold ${t.operatingProfit >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                        {formatCurrency(t.operatingProfit)}
                      </td>
                      {showPercentColumn && <td className="min-w-[60px]"></td>}
                    </React.Fragment>
                  ))}
                  <td className={`min-w-[120px] px-2 py-2 text-right text-sm font-bold ${projectedProfit >= 0 ? 'text-green-700 bg-green-100' : 'text-red-700 bg-red-100'}`}>
                    {/* Calculate from per-unit projection totals */}
                    {projectedGross > 0 ? formatCurrency(projectedProfit) : '--'}
                  </td>
                </tr>
              )}

              {/* MISC Department: Total Misc */}
              {dept.name === 'MISC' && (
                <tr className={ROW_COLORS.totalMisc}>
                  <td className={`min-w-[200px] px-3 py-2 text-sm font-bold sticky left-0 ${ROW_COLORS.totalMisc}`}>
                    Total Misc
                  </td>
                  {colTotals.map((t, i) => (
                    <React.Fragment key={i}>
                      <td className="min-w-[100px] px-2 py-2 text-right text-sm font-bold">
                        {formatCurrency(t.totalExpenses)}
                      </td>
                      {showPercentColumn && <td className="min-w-[60px]"></td>}
                    </React.Fragment>
                  ))}
                  <td className={`min-w-[120px] px-2 py-2 text-right text-sm ${ROW_COLORS.totalMisc}`}>--</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
